/**
 * @fileoverview Linha de configuração: título, descrição curta e o controle à
 * direita (interruptor, select ou o que vier em `children`). Mostra o retorno de
 * quem salva sozinho: carregando enquanto grava e "Salvo" logo depois.
 *
 * Props principais:
 *  - id: vira data-setting-id (a busca da área rola e foca por ele)
 *  - toggle: interruptor; value "enabled"/"disabled"
 *  - options: [{ value, label }] → select padrão do app (ModernSelect)
 *  - children: controle próprio (substitui interruptor/select)
 *  - stacked: controle embaixo do texto (campos largos, como textos longos)
 *  - wide: controle próprio largo, que no celular vai para baixo do texto
 *  - divider: linha em cima mesmo sem uma linha irmã antes (ex.: num Collapse)
 *  - loading / saved: estado da gravação (saved = momento em que salvou)
 */

import React, { useEffect, useState } from "react";
import { CircularProgress, MenuItem, Switch } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Check } from "lucide-react";

import ModernSelect from "../ModernSelect";
import { tokens } from "../Triage/styles";
import { i18n } from "../../translate/i18n";

const SAVED_VISIBLE_MS = 2000;

const useStyles = makeStyles((theme) => {
  const c = tokens(theme.palette.type === "dark");
  const xs = theme.breakpoints.down("xs");
  return {
    row: {
      display: "flex",
      alignItems: "center",
      gap: 16,
      padding: "14px 20px",
      "& + &": { borderTop: `1px solid ${c.border}` },
      [xs]: { padding: "12px 16px" },
    },
    // Linha que não é irmã da anterior no DOM (ex.: dentro de um Collapse).
    divider: { borderTop: `1px solid ${c.border}` },
    rowStacked: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: 10,
    },
    rowWideOnPhone: {
      [xs]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: 10,
        "& $control": { width: "100%" },
      },
    },
    text: { flex: 1, minWidth: 0 },
    titleLine: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    title: {
      fontSize: 14,
      fontWeight: 600,
      color: c.textPrimary,
      lineHeight: 1.4,
    },
    description: {
      marginTop: 2,
      fontSize: 12.5,
      color: c.textSecondary,
      lineHeight: 1.5,
    },
    control: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 10,
      flexShrink: 0,
      minWidth: 0,
    },
    controlStacked: { display: "block", width: "100%", flexShrink: 1 },
    select: {
      minWidth: 200,
      [xs]: { minWidth: 0, width: "100%" },
    },
    status: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 12,
      color: c.textMuted,
      whiteSpace: "nowrap",
    },
    spinner: { color: c.textMuted },
  };
});

// "Salvo" só aparece logo depois de gravar (voltar à seção não mostra de novo).
const SaveStatus = ({ classes, loading, saved }) => {
  const [recent, setRecent] = useState(false);

  useEffect(() => {
    if (!saved) return undefined;
    const left = SAVED_VISIBLE_MS - (Date.now() - saved);
    if (left <= 0) return undefined;
    setRecent(true);
    const timer = setTimeout(() => setRecent(false), left);
    return () => clearTimeout(timer);
  }, [saved]);

  if (loading) return <CircularProgress size={14} className={classes.spinner} aria-label={i18n.t("settings.area.saving")} />;
  if (!recent) return null;
  return (
    <span className={classes.status} role="status">
      <Check size={14} strokeWidth={2} aria-hidden="true" />
      {i18n.t("settings.area.saved")}
    </span>
  );
};

const SettingItem = ({
  id,
  title,
  description,
  value,
  onChange,
  options,
  toggle = false,
  disabled = false,
  stacked = false,
  wide: wideControl = false,
  divider = false,
  loading = false,
  saved,
  children,
}) => {
  const classes = useStyles();
  const titleId = id ? `setting-${id}-title` : undefined;
  const descriptionId = id && description ? `setting-${id}-description` : undefined;
  // Controle largo (select, campos lado a lado) vai para baixo do texto no celular.
  const wide = stacked || wideControl || (!toggle && !!options);

  let control = children;
  if (!control && toggle) {
    control = (
      <Switch
        color="primary"
        checked={value === "enabled"}
        onChange={(event) => onChange(event.target.checked ? "enabled" : "disabled")}
        disabled={disabled}
        inputProps={{ "aria-labelledby": titleId, "aria-describedby": descriptionId }}
      />
    );
  } else if (!control && options) {
    control = (
      <ModernSelect
        size="small"
        className={classes.select}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        labelId={titleId}
        MenuProps={{ disableScrollLock: true }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </ModernSelect>
    );
  }

  return (
    <div
      className={[
        classes.row,
        stacked ? classes.rowStacked : "",
        wide && !stacked ? classes.rowWideOnPhone : "",
        divider ? classes.divider : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-setting-id={id}
    >
      <div className={classes.text}>
        <div className={classes.titleLine}>
          <div className={classes.title} id={titleId}>
            {title}
          </div>
          {stacked && <SaveStatus classes={classes} loading={loading} saved={saved} />}
        </div>
        {description && (
          <div className={classes.description} id={descriptionId}>
            {description}
          </div>
        )}
      </div>
      <div className={[classes.control, stacked ? classes.controlStacked : ""].filter(Boolean).join(" ")}>
        {!stacked && <SaveStatus classes={classes} loading={loading} saved={saved} />}
        {control}
      </div>
    </div>
  );
};

export default SettingItem;
